import { Paper, TableBody, TableContainer, TableHead, TableRow, Theme, useMediaQuery } from "@mui/material"
import { DefaultButton, SettingTable, TableCellButton } from "../Settings"
import TextCell from "../components/TextCell"
import { ISliderInterface } from "../components/SliderCell"
import SliderRow from "../components/SliderRow"
import { useState } from "react"
import ConfirmationDialog from "../../../components/Misc/Dialog/ConfirmationDialog"
import SwitchCell from "../components/SwitchCell"
import { useSnackbar } from "notistack";

interface IAttributesProps {
    findDefaultFVSliderInterface: (name: string) => ISliderInterface | undefined,
    findFVSliderInterface: (name: string) => (ISliderInterface | undefined),
    setFVSliderInterfaceOption: (op: ISliderInterface, basic?: boolean | undefined) => void
    resetValueToDefault: (name: string) => void,
    setOption: (key: string, value: string | number | boolean, basic?: boolean) => void
    icaoCompliance: boolean,
    attributesActive: boolean
}

const Attributes: React.FC<IAttributesProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const [openDialog, setOpenDialog] = useState<boolean>(false)
    const [active, setActive] = useState(props.attributesActive || props.icaoCompliance)

    const setAttributes = (attributes: boolean) => {
        if (props.icaoCompliance) {
            enqueueSnackbar("Cannot disable attributes when ICAO is enabled", {variant: "warning"})
            return
        }
        setActive(attributes)
        props.setOption("DetectProperties", attributes)
    }

    const handleResetClick = () => {
        props.resetValueToDefault("IcaoBlinkThreshold")
        props.resetValueToDefault("IcaoGlassesThreshold")
        props.resetValueToDefault("IcaoDarkGlassesThreshold")
        props.resetValueToDefault("IcaoExpressionThreshold")
        props.resetValueToDefault("IcaoMouthOpenThreshold")
        props.resetValueToDefault("IcaoHatThreshold")
    }

    return (
        <>
        <TableContainer component={Paper} style={{marginBottom: "2rem", tableLayout: "fixed"}}>
            <SettingTable>
                <TableHead>
                    <TableRow>
                        <TextCell head title="Attributes" subtitle=""/>
                        <TableCellButton align="right" colSpan={isMobile? 1 : 2}>
                            <DefaultButton variant="text" color="primary" onClick={() => setOpenDialog(true)}>
                                Reset to default
                            </DefaultButton>
                        </TableCellButton>
                        <SwitchCell state={active} setState={setAttributes}/>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Blink"
                        subtitle="Determines whether eyes are closed or opened."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoBlinkThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoBlinkThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="raising"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100] with higher values indicating that person is blinking."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Glasses"
                        subtitle="Determines whether glasses are present or not."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoGlassesThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoGlassesThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating probability that person is wearing some sort of glasses."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Dark glasses"
                        subtitle="Determines whether glasses are transparent or dark."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoDarkGlassesThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoDarkGlassesThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100] with higher values indicating probability that dark glasses are present."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Expression"
                        subtitle="Determines if the face exhibits a neutral expression."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoExpressionThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoExpressionThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100] with higher values indicating that person is smiling."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Mouth open"
                        subtitle="Determines whether mouth is opened or not."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoMouthOpenThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoMouthOpenThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces value on [0, 100] with higher values indicating probability that mouth is opened. In theory this value should be a percentage."
                    />
                    <SliderRow
                        mobile={isMobile}
                        textTitle="Hat (headwear)"
                        subtitle="Determines whether hat is present or not."
                        title="Threshold range"
                        option={props.findFVSliderInterface("IcaoHatThreshold")}
                        defaultOption={props.findDefaultFVSliderInterface("IcaoHatThreshold")}
                        setOption={props.setFVSliderInterfaceOption}
                        minValue={0}
                        maxValue={100}
                        type="falling"
                        active={active}
                        lockMaxValue
                        info="Produces a value on [0, 100] with higher values indicating that person is wearing some kind of a head covering."
                    />
                </TableBody>
            </SettingTable>
        </TableContainer>
        <ConfirmationDialog
            open={openDialog}
            setOpen={setOpenDialog}
            title="Reset attributes settings"
            text="Reset attributes settings to default values?"
            cancelText="Cancel"
            confirmText="Yes"
            confirm={handleResetClick}
            confirmColor="error"
        />
        </>
    )
}

export default Attributes